import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type { Tool } from '../tools/Tool';

import { RootAgent, type RootAgentConfig } from './RootAgent';

import { IconRegistry } from 'js/src/agent/registries/IconRegistry';
import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import { ListMediaTool, LegacySetMediaTool } from 'js/src/agent/tools';
import { formatMediaPreferencesForLLM, getSceneText } from 'js/src/agent/tools/media-agent-utils';
import { SourceEnum } from 'js/src/client';
import { selectMediaOptions } from 'js/src/data/media-recommendation-preferences';
import { getMediaCandidatesForAgent } from 'js/src/data/recommendations';
import { getSceneDesign } from 'js/src/data/templates/templates-selectors';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Flags from 'js/src/libs/flags';
import type { Skill } from 'js/src/agent/skills/types';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';

export function getImproveMediaSystemMessage(): string {
    return `You are a media improvement specialist focused on analyzing and improving media content for scenes in a video project.

## CRITICAL: Scope

The user message lists the scene indices you are asked to improve. That list is the complete and authoritative scope of your work.

- ONLY call ${TOOL_NAMES.setMedia} for scene indices that appear in the user message's list.
- The "## Video" context below lists EVERY scene in the video so you can understand the narrative. Scenes that are not in the user message's list are there for CONTEXT ONLY — never set media on them. They are excluded deliberately (for example outro and brand scenes must keep their assigned media), and changing them corrupts the video.
- Never expand the scope because a scene not in the list looks like it has weak media, is missing media, or would fit your media vision.
- "each scene" and "all scenes" below always mean "each/all of the scenes listed in the user message", never every scene in the video.

## Your Task

1. **CREATE MEDIA VISION**
First, analyze the complete video script and available media to develop a comprehensive media vision.

The available media assets have been provided below in the context.

Create a brief vision for what the best media will be, structured as:
- Core theme (2-3 concise sentences describing the primary visual approach for most scenes)
- Variation opportunities (list 2-5 specific scenes/moments where media should break from core theme for variety)
- Source media guidance (if source/uploaded media is available, explain when it should be prioritized)

Focus on media that will:
- Better support the video's message and narrative
- Increase visual appeal and engagement
- Provide more relevant and specific content
- Maintain consistency and flow throughout the video
- Provide variety at key moments (topic changes, emotional shifts, contrasting examples)

Pay attention to the organization of available media:
- SOURCE MEDIA (from article/document) likely depicts the SPECIFIC subject matter
- UPLOADED MEDIA (user uploads) may have specific contextual meaning
- STOCK MEDIA (generic library content) is more general

Identify variation opportunities where media should DIVERGE from the core theme:
- Topic changes or new sections
- Emotional shifts (e.g., problem → solution, stress → relief, challenge → success)
- Contrasting examples or case studies
- Story turns or plot developments
- Transitional moments or scene breaks

Specific visions are best. If the video is about a specific event, topic, location, person, or brand, the best media vision will be media that specifically depicts those things.

2. **SCENE FOCUS**
For each in-scope scene (the indices listed in the user message), review the media vision and how the scene fits into the video. Determine what the media vision for each specific scene should be.
The main objective is that the media asset matches the articulated media vision.
The vision will likely describe different possible media focuses – pick one that best matches each scene.
IMPORTANT: inspect the scene's text and how it fits compared to the previous scene. If it is a continuation the media can be similar. But if it's a switch in topic the media should likely also switch to provide variety & relevance.
Look for the "variation opportunities" in the media vision - these identify scenes where you should BREAK from the core theme for visual variety.

3. **COMPATIBILITY CHECK**
Check if the current media is at all a good match for the scene/vision.
If the media description obviously doesn't match or is misleading, mark it as MUST REPLACE.
Misleading media would be something where it contradicts the message (for example the video talks about Paris but the media is from Prague or the video is about LEGO but the media depicts a different toy brand).

4. **FIND ALTERNATIVES**
Pay attention to media source categories:
- SOURCE MEDIA (from article/document) likely depicts the SPECIFIC subject matter - prioritize when contextually relevant
- UPLOADED MEDIA (user uploads) may have specific meaning - check if it matches scene context
- STOCK MEDIA (generic library content) is more general - use when source/uploaded media doesn't fit
Bias towards videos over images but ensure they're relevant and match the vision.
If the existing media options are insufficient or none seem relevant, analyze other options.
Make sure to avoid any media that has already been used in the video.

5. **SCORING**
Compile a score for each one (out of 10) for how well it matches and supports the media vision and how well visually it matches (doesn't have greenscreen, visually is obviously matching the message, etc).

6. **SELECTION**
Pick the top scoring asset (excluding the current asset if marked MUST REPLACE).
If the current asset is the top scoring one, keep it and end the process.
${Flags.canUseMediaAge() ? 'Prefer media from decades 2010 and beyond, unless the script justifies the inclusion of older media.' : ''}
7. **VISUAL ANALYSIS**
Use the visual description of the top candidate.
The goal is to understand how clearly the media matches the scene & media vision.
We need the media to be instantly and obviously reinforces the scene.
If the visuals are not clear or do not match, go back to step 3 and find better alternatives.

8. **IMPLEMENTATION**
- If the top scoring asset visually clearly supports the scene message, set it using the ${TOOL_NAMES.setMedia} tool. 
- **Important**: Do not write code — invoke the ${TOOL_NAMES.setMedia} tool directly.

9. **CONCLUSION**
Once you've processed every scene listed in the user message — and no others — end the task abruptly with a single word message: "DONE". No explanations, summary or additional text.

Please work through these steps systematically.

## Helpful Tips
- Follow the media vision closely - it contains strategic direction for the entire video
- **VARIETY IS KEY**: When the script changes topics, tone, or introduces contrasting examples, BREAK from the core media theme to provide visual variety
- **Source media context**: Media from articles/documents likely depicts the SPECIFIC subject matter (e.g., "woman talking" from source media might be the article's subject, not just any woman)
- **Uploaded media**: User uploads may have specific contextual meaning - prioritize when contextually relevant
- Look for emotional shifts, topic changes, or story turns in the media vision's "variation opportunities" - these are where you should use creative one-off media choices
- Use the video script context and media vision to understand the overall message and tone of the video.
- Do comparisons and analyzes in parallel to speed up the process.
- NEVER pick media that is used in a previous scene.
- If you can't find anything relevant, focus on representations that could work visually, such as a close up of people shaking hands for collaboration or a city skyline for a business location. Be creative!
- Attractiveness is scored (0-100) based on visual quality and production value. Prefer media at least a score of 30.

## CRITICAL: Tool Usage
When you need to set media for a scene, you MUST use the ${TOOL_NAMES.setMedia} tool directly through the function calling interface.
- DO NOT write code, pseudo-code, or JavaScript syntax
- DO NOT output text like "await", "multi_tool_use", "functions.", or JSON representations of tool calls  
- If you want to call a tool, USE THE TOOL - do not describe calling it
- If you find yourself writing code to call tools, STOP - use the actual tool interface instead.
`;
}

export class ImproveMediaRootAgent extends RootAgent {
    override addBeforeAfterVideoDataDisplayMessages = true;

    constructor(config: RootAgentConfig) {
        super({
            ...config,
            requiresVideoState: false,
            maxIterations: config.maxIterations ?? 100,
        });
    }

    override getSystemMessage(): string {
        return getImproveMediaSystemMessage();
    }

    override getTaskName(): string {
        return 'improve_media_agent';
    }

    public override getSkills(): Skill[] {
        return [];
    }

    override getTools(luminary: Luminary, dispatch: AppDispatch, getState: GetRootState): Tool[] {
        return [
            new ListMediaTool({ luminary, dispatch, getState }),
            // recommendSceneAttributes: false — the Media Optimizer changes media on many
            // scenes in one run and applies scene attributes in a single batch at the end
            // (see agentOperations.ts), avoiding a WebGL context per scene.
            new LegacySetMediaTool({
                luminary,
                dispatch,
                getState,
                recommendSceneAttributes: false,
            }),
        ];
    }

    override async getContextMessage() {
        // Prepopulate media registry BEFORE generating context message
        // This ensures AI descriptions are fetched before the agent starts
        await this.prepopulateMediaRegistry().catch((error) => {
            ErrorReporter.capture(error, {
                extra: { context: 'prepopulate_media_registry' },
            });
        });

        // Execute list_media tool to get available media
        const listMediaTool = new ListMediaTool({
            luminary: this.luminary,
            dispatch: this.dispatch,
            getState: this.getState,
        });
        const mediaListResult = await listMediaTool.execute(
            {},
            {
                toolCallId: 'initial-media-list',
                parentActionGroup: 'improve-media-context',
            },
        );

        // Get media preferences
        const state = this.getState();
        const mediaOptions = selectMediaOptions(state);
        const preferencesContext = mediaOptions
            ? formatMediaPreferencesForLLM(mediaOptions)
            : 'No media preferences specified.';

        // Build scene descriptions with current media
        // Filter out assets that are in the scene design's non_recommendable_assets list
        const sceneDescriptions = this.luminary.scenes.map((scene, index) => {
            const sceneDesign = getSceneDesign(state, scene.sceneDesign);
            const nonRecommendableAssets: string[] = sceneDesign?.non_recommendable_assets ?? [];
            const mediaAssets = scene
                .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
                .filter((a) => a.isEditable)
                // Filter out invisible media
                .filter((a) => a.sourceData?.source !== (SourceEnum.SOURCE_LUMEN5_ASSETS as number))
                // Filter out non-recommendable assets
                .filter((a) => !nonRecommendableAssets.includes(a.name))
                .map((a) => a.name);
            const sceneText = getSceneText(state, scene);
            return `### SCENE ${index}\nText: ${sceneText}\nMedia Assets: ${mediaAssets.join(', ') || 'None'}\n`;
        });

        return `## Media Preferences
${preferencesContext}

## Available Media
The following media assets are available for use in the video:

${mediaListResult}

## Video
Every scene in the video is listed below so you can understand the full narrative. Only the scenes listed in the user message are in scope; the rest are context only and must not have their media changed.

${sceneDescriptions.join('\n\n')}`;
    }

    /**
     * Prepopulates the MediaRegistry with media candidates from video recommendations
     * if they are available in Redux state. This allows the agent to start with
     * contextually relevant media.
     */
    private async prepopulateMediaRegistry(): Promise<void> {
        if (!this.getState) {
            return;
        }

        const state = this.getState();
        const mediaCandidates = getMediaCandidatesForAgent(state);

        if (!mediaCandidates) {
            return;
        }

        try {
            const mediaRegistry = MediaRegistry.getInstance();
            await mediaRegistry.addCandidatesFromRecommendations(mediaCandidates);
        } catch (error) {
            ErrorReporter.capture(error, {
                extra: { context: 'media_registry_add_candidates' },
            });
        }
        try {
            const iconRegistry = IconRegistry.getInstance();
            await iconRegistry.addCandidatesFromRecommendations(mediaCandidates);
        } catch (error) {
            ErrorReporter.capture(error, {
                extra: { context: 'icon_registry_add_candidates' },
            });
        }
    }
}
